/**
 * Sparkline — Tufte's "intense, simple, word-sized graphic."
 *
 * Inline ~100×20px SVG line chart. No axes, no gridlines, no decoration —
 * just the data trace + an optional terminal dot + an optional band of
 * "normal range" shading. Designed to live inside a table row, a
 * member card, a stat card footer.
 *
 * Use cases in CongressMCP:
 *   - per-member voting trend (last 20 votes as ±)
 *   - cosponsor count growth on a tracked bill
 *   - relationship-strength score over time
 *   - donation cadence per donor row
 *   - whip-count drift week over week (one sparkline per chamber)
 *
 * Anti-patterns it deliberately doesn't support:
 *   - multiple series (use Tufte slope-graph or small multiples instead)
 *   - axis labels (the surrounding number/text IS the label)
 *   - tooltips on hover (data-density beats interactivity here)
 *
 * Width/height defaults match Tufte's recommendation: ~100×20 sits
 * naturally next to one line of body text without disrupting the
 * baseline grid.
 */

import React from "react";
// React 19 types no longer declare a global JSX namespace.
import type { JSX } from "react";

export interface SparklineProps {
  /** Series of numbers to plot. Positions evenly along the x-axis. */
  values: number[];
  /** Pixel width. Default 100. */
  width?: number;
  /** Pixel height. Default 20. */
  height?: number;
  /**
   * Optional [low, high] range to render as a faint horizontal band —
   * Tufte's "normal range" overlay. Useful for things like "agreement_pct
   * normally between 70 and 90." When omitted, no band renders.
   */
  band?: [number, number];
  /** Color of the line. Default currentColor (inherits from text color). */
  stroke?: string;
  /**
   * Render a small dot at the last point. Default true — the eye lands on
   * it, the rest of the trace is the trajectory toward it.
   */
  showTerminal?: boolean;
  /** Optional accessible label. Read by screen readers in place of the SVG. */
  label?: string;
}

export function Sparkline({
  values,
  width = 100,
  height = 20,
  band,
  stroke = "currentColor",
  showTerminal = true,
  label,
}: SparklineProps): JSX.Element | null {
  if (!values || values.length === 0) return null;

  // Compute extents from data (NOT the band — band is informational
  // overlay, not chart bounds). Tufte: data-driven y axis means small
  // movements in the data are visible at the chart's scale.
  const scale = Math.max(1, ...values.map(Math.abs));
  const min = Math.min(...values.map((value) => value / scale));
  const max = Math.max(...values.map((value) => value / scale));
  // Avoid divide-by-zero when all points are equal — render a flat line
  // through the vertical midpoint.
  const range = max - min || 1;

  const padX = 1; // tiny inset so the terminal dot doesn't clip
  const padY = 2;
  const innerW = width - padX * 2;
  const innerH = height - padY * 2;

  const x = (i: number): number =>
    padX + (values.length === 1 ? innerW / 2 : (i / (values.length - 1)) * innerW);
  const y = (v: number): number => max === min ? height / 2 : padY + innerH - ((v / scale - min) / range) * innerH;

  const points = values.map((v, i) => `${x(i)},${y(v)}`).join(" ");

  // Band: convert [low, high] into a rect at the data scale. If the band
  // is entirely outside the data, it'll render off-canvas — that's fine,
  // the user wanted to flag the discrepancy.
  let bandRect: JSX.Element | null = null;
  if (band) {
    const [bLo, bHi] = band;
    const yLo = y(bHi); // higher value → smaller y
    const yHi = y(bLo);
    bandRect = (
      <rect
        x={padX}
        y={yLo}
        width={innerW}
        height={Math.max(0, yHi - yLo)}
        fill="currentColor"
        opacity={0.07}
      />
    );
  }

  const last = values[values.length - 1];
  const lastX = x(values.length - 1);
  const lastY = y(last);

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label={label ?? `Sparkline of ${values.length} points, last value ${last}`}
      style={{ display: "inline-block", verticalAlign: "middle" }}
    >
      {bandRect}
      <polyline
        points={points}
        fill="none"
        stroke={stroke}
        strokeWidth={1}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {showTerminal && (
        <circle cx={lastX} cy={lastY} r={1.6} fill={stroke} />
      )}
    </svg>
  );
}
